import { useNavigate } from 'react-router-dom'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'

import { selectDisconnectedAccountCount } from '@/selectors/financeSelectors'
import { useReselector } from '@/utils/sharedHooks'
import { useAnalyticsTrack } from '@/features/amplitude'
import { defaultMessagesURL } from '@/features/zendesk/helpers'
import { getUserNeedsClarificationTransactionsSelector } from '@/features/transactions/transactions.selectors'
import { getAllActiveUserActionItems } from '@/features/user/dashboard/user-action-items/lib/userActionItems.selectors'
import { selectYourTurnNumber } from '@/components/Conversations/conversation.slice'

import { TypographyStyles } from '@/styles'
import { Button, Card, CardColors, Icon, Text } from '@/ui'
import { isEmailVerified } from '@/selectors/user.selectors'

const SummaryLabel = ({
  onClick,
  text,
  icon,
}: {
  onClick: () => void
  text: string
  icon: IconDefinition
}) => (
  <Button onClick={onClick} variant="secondary" className="h-8">
    <Icon icon={icon} color="text-neutral-500" scale={0.75} />
    <Text variant={TypographyStyles.BODY_XS}>{text}</Text>
    <Icon icon={regular('arrow-right')} color="text-neutral-500" scale={0.75} />
  </Button>
)

export const SummaryActions = () => {
  const navigate = useNavigate()
  const track = useAnalyticsTrack()

  const messageCount = useReselector(selectYourTurnNumber)
  const userEmailVerified = useReselector(isEmailVerified)
  const activeItems = useReselector(getAllActiveUserActionItems)
  const disconnectedAccountCount = useReselector(selectDisconnectedAccountCount)
  const needsClarificationTransactions = useReselector(
    getUserNeedsClarificationTransactionsSelector
  )

  const hasNoItems =
    !messageCount &&
    !activeItems.length &&
    !disconnectedAccountCount &&
    !needsClarificationTransactions.length

  return (
    <div className="flex flex-col">
      <Text variant={TypographyStyles.LABEL_SM} className="w-full">
        Your Summary
      </Text>

      {hasNoItems ? (
        <Card color={CardColors.LEVEL_0}>
          <Text variant={TypographyStyles.BODY_SM} textAlign="center">
            <Icon icon={regular('party-horn')} style={{ marginRight: 8 }} />
            You&apos;re all caught up! No unread messages, to dos or
            transactions to review.
          </Text>
        </Card>
      ) : (
        <div className="flex mt-1 gap-2">
          {Boolean(disconnectedAccountCount) && (
            <SummaryLabel
              icon={regular('bank')}
              text={`Disconnected Accounts: (${disconnectedAccountCount})`}
              onClick={() => {
                track('Clicked Your Summary Disconnected Accounts Button')
                navigate('/accounts#connected-institutions')
              }}
            />
          )}

          {Boolean(messageCount) && userEmailVerified && (
            <SummaryLabel
              icon={regular('envelope')}
              text={`Unread Messages: (${messageCount})`}
              onClick={() => {
                track('Clicked Your Summary Messages Button')
                window.location.href = defaultMessagesURL
              }}
            />
          )}

          {Boolean(activeItems.length) && (
            <SummaryLabel
              icon={regular('check-square')}
              text={`To Dos: (${activeItems.length})`}
              onClick={() => {
                track('Clicked Your Summary To Dos Button')
                window.location.href = '#dashboard-todos'
              }}
            />
          )}

          {Boolean(needsClarificationTransactions.length) && (
            <SummaryLabel
              icon={regular('receipt')}
              text={`Transactions to Review: (${needsClarificationTransactions.length})`}
              onClick={() => {
                track('Clicked Your Summary Transactions Button')
                navigate('/transactions/review')
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
